/**
 * F5 — NETWORK: PURE aggregation of subtree reports (roll-up of a network /
 * denomination over member churches) using materialized path.
 *
 * NO client-side recursion: subtree resolves via path prefix
 * (`isInSubtree`), SAME clause governing sync. Respects permissions: a
 * node only enters metric aggregate if account has corresponding
 * grant on that node (people/attendance/finances). "Data presence =
 * permission" (F3), but filtering by allowed set makes it explicit
 * and testable: a subtree church WITHOUT grant contributes 0 to total.
 *
 * Full design: docs/SUBTREE_REPORTS.md + docs/NETWORK_FEDERATION.md.
 */

import { type FinanceRecordLike, financeSummary } from "../finance/summary";
import {
  attendanceOverview,
  attendanceSeries,
  type AttendanceSessionLike,
} from "../stats/attendance";
import {
  type MembershipBreakdown,
  membershipBreakdown,
  type MembershipLike,
} from "../stats/membership";
import { isInSubtree, pathDepth, pathToAncestorIds } from "../permissions/tree";

/** Minimal church row for network roll-up (with materialized path). */
export interface NetworkChurch {
  id: string;
  name: string | null;
  path: string | null;
  parent_id: string | null;
}

/** Effective path of row (own id if server-side path not yet arrived). */
function churchPath(church: NetworkChurch): string {
  return church.path && church.path.length > 0 ? church.path : church.id;
}

/**
 * Churches in subtree of `rootId` (includes root), resolved via path
 * prefix — without recursion. Stable order by path (root -> leaves).
 */
export function churchesInSubtree<T extends NetworkChurch>(
  churches: readonly T[],
  rootId: string,
): T[] {
  const root = churches.find((c) => c.id === rootId);
  if (!root) {
    return [];
  }
  const rootPath = churchPath(root);
  return churches
    .filter((c) => isInSubtree(rootPath, churchPath(c)))
    .toSorted((a, b) => churchPath(a).localeCompare(churchPath(b)));
}

/** Empty membership breakdown (church without `people` grant). */
const EMPTY_MEMBERSHIP: MembershipBreakdown = {
  total: 0,
  byStatus: [],
  baptized: 0,
  serving: 0,
  leaders: 0,
  pastors: 0,
};

export interface SubtreeReportInput {
  rootId: string;
  /** ALL synchronized churches (subtree trimmed by path). */
  churches: readonly NetworkChurch[];
  memberships: readonly MembershipLike[];
  sessions: readonly AttendanceSessionLike[];
  finances: readonly FinanceRecordLike[];
  /** Churches where account has `people:read` (allowed subset). */
  membershipChurchIds: ReadonlySet<string>;
  /** Churches where account has `attendance:read`. */
  attendanceChurchIds: ReadonlySet<string>;
  /** Churches where account has `finances:read`. */
  financeChurchIds: ReadonlySet<string>;
}

/** Roll-up of ONE church in subtree (metrics zeroed if grant missing). */
export interface ChurchRollup {
  churchId: string;
  name: string | null;
  path: string;
  /** Depth relative to network root (root = 0) — for indentation. */
  depth: number;
  hasMembership: boolean;
  hasAttendance: boolean;
  hasFinance: boolean;
  members: MembershipBreakdown;
  attendanceTotal: number;
  attendanceSessions: number;
  attendanceAverage: number;
  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;
  financeCount: number;
  currency: string | null;
}

export interface SubtreeTotals {
  members: number;
  baptized: number;
  serving: number;
  leaders: number;
  pastors: number;
  attendanceTotal: number;
  attendanceSessions: number;
  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;
}

export interface SubtreeReport {
  rootId: string;
  /** Visible subtree churches (those synchronized by account). */
  churchCount: number;
  /** Churches contributing to ANY metric (having at least one grant). */
  scopedChurchCount: number;
  perChurch: ChurchRollup[];
  totals: SubtreeTotals;
  /** Currency of first finance record found (homogeneous breakdown). */
  currency: string | null;
}

function currencyOf(finances: readonly FinanceRecordLike[], churchId: string): string | null {
  return finances.find((r) => r.church_id === churchId && r.currency)?.currency ?? null;
}

/**
 * Aggregated report of a church subtree, respecting permissions per metric.
 * PURE FUNCTION: (churches with path, synced data, allowed sets)
 * -> totals + breakdown per church. Node without metric grant
 * contributes 0 to that metric and is marked (`hasX = false`).
 */
export function subtreeReport(input: SubtreeReportInput): SubtreeReport {
  const subtree = churchesInSubtree(input.churches, input.rootId);
  const root = subtree.find((c) => c.id === input.rootId);
  const rootDepth = root ? pathDepth(churchPath(root)) : 0;

  const totals: SubtreeTotals = {
    members: 0,
    baptized: 0,
    serving: 0,
    leaders: 0,
    pastors: 0,
    attendanceTotal: 0,
    attendanceSessions: 0,
    incomeMinor: 0,
    expenseMinor: 0,
    netMinor: 0,
  };
  let currency: string | null = null;
  let scopedChurchCount = 0;

  const perChurch: ChurchRollup[] = subtree.map((church) => {
    const hasMembership = input.membershipChurchIds.has(church.id);
    const hasAttendance = input.attendanceChurchIds.has(church.id);
    const hasFinance = input.financeChurchIds.has(church.id);
    if (hasMembership || hasAttendance || hasFinance) {
      scopedChurchCount += 1;
    }

    const members = hasMembership
      ? membershipBreakdown(input.memberships, church.id)
      : EMPTY_MEMBERSHIP;
    const attendance = hasAttendance
      ? attendanceOverview(attendanceSeries(input.sessions, "month", { churchId: church.id }))
      : { sessions: 0, total: 0, average: 0 };
    const finance = hasFinance
      ? financeSummary(input.finances, { churchId: church.id })
      : { incomeMinor: 0, expenseMinor: 0, netMinor: 0, count: 0 };

    if (hasMembership) {
      totals.members += members.total;
      totals.baptized += members.baptized;
      totals.serving += members.serving;
      totals.leaders += members.leaders;
      totals.pastors += members.pastors;
    }
    if (hasAttendance) {
      totals.attendanceTotal += attendance.total;
      totals.attendanceSessions += attendance.sessions;
    }
    if (hasFinance) {
      totals.incomeMinor += finance.incomeMinor;
      totals.expenseMinor += finance.expenseMinor;
      totals.netMinor += finance.netMinor;
      currency = currency ?? currencyOf(input.finances, church.id);
    }

    return {
      churchId: church.id,
      name: church.name,
      path: churchPath(church),
      depth: Math.max(0, pathToAncestorIds(churchPath(church)).length - 1 - rootDepth),
      hasMembership,
      hasAttendance,
      hasFinance,
      members,
      attendanceTotal: attendance.total,
      attendanceSessions: attendance.sessions,
      attendanceAverage: attendance.average,
      incomeMinor: finance.incomeMinor,
      expenseMinor: finance.expenseMinor,
      netMinor: finance.netMinor,
      financeCount: finance.count,
      currency: hasFinance ? currencyOf(input.finances, church.id) : null,
    };
  });

  return {
    rootId: input.rootId,
    churchCount: subtree.length,
    scopedChurchCount,
    perChurch,
    totals,
    currency,
  };
}
